import type { Capitolo } from '../tipi.ts'

const capitolo: Capitolo = {
  id: 'utenti',
  titolo: 'Utenti e incarichi',
  gruppo: 'Report e gestione',
  permesso: 'utente.gestisci',
  aCheServe: 'Per chi gestisce gli utenti: far entrare una persona nel gestionale, darle gli incarichi giusti e '
    + 'chiuderle l’accesso quando non lavora più con CIDIS. Tutto da «Amministrazione» → «Utenti».',
  parti: [
    {
      titolo: '«Aggiungi una persona» in tre passi',
      passi: [
        'Premi «Aggiungi una persona». Passo 1: nome, cognome, «Email personale» (dove arrivano l’invito e poi il '
          + 'recupero della password), «Rapporto con CIDIS» (per un esterno anche la scadenza, al massimo dodici mesi), '
          + '«È esterno?» per i collaboratori esterni, «Crea la casella CIDIS», sedi e lingue. Premi «Salva e prepara '
          + 'l’invito».',
        'Passo 2: assegna gli incarichi (vedi sotto) e premi «Avanti: consegna l’invito».',
        'Passo 3: la pagina dice se l’email è partita. Manda il collegamento come preferisci e **detta il codice '
          + 'di sei cifre a voce**, mai nello stesso messaggio. L’invito vale 24 ore.',
      ],
      immagine: {
        file: 'utenti-aggiungi.webp', larghezza: 1200, altezza: 800,
        descrizione: 'La finestra Aggiungi una persona, passo 1 di 3, con i dati della persona.',
      },
    },
    {
      titolo: 'La casella CIDIS della persona nuova',
      testo: [
        '«Crea la casella CIDIS» è già spuntata per i rapporti interni e spenta per esterni e mediatori; la scegli tu. '
          + 'Con la spunta la persona entra con l’indirizzo CIDIS e trova la sua casella in «Posta»; l’invito parte alla '
          + 'sua email personale.',
        'L’indirizzo proposto è l’iniziale del nome e il cognome, senza accenti, apostrofi e spazi: «Davide Ramondetti» '
          + '→ dramondetti@cidisonlus.org. Puoi scriverne un altro. Se è già preso da un’altra casella o da un’altra '
          + 'persona, sotto il campo leggi da che cosa e alcuni indirizzi liberi da scegliere.',
        'Senza la spunta, come prima: la persona entra con l’email personale.',
      ],
    },
    {
      titolo: 'Collaboratori esterni: «È esterno?»',
      testo: [
        'Per i collaboratori esterni che non devono avere la casella cidisonlus.org spunta **«È esterno?»** nel '
          + 'passo 1. Il rapporto diventa «Esterno» (la scadenza dell’utenza è facoltativa) e la persona entra con l’iniziale del '
          + 'nome e il cognome **@esterni.cidisonlus.org**, per esempio mrossi@esterni.cidisonlus.org: stessa proposta e '
          + 'stessi controlli della casella CIDIS, e puoi cambiarlo. L’invito parte alla sua email personale.',
        'La persona trova in «Posta» la sua casella @esterni, che serve alle comunicazioni interne: scrive e riceve '
          + 'solo dentro CIDIS. Chi le scrive da fuori riceve un rifiuto; se lei scrive a un indirizzo fuori da '
          + 'CIDIS, il gestionale lo dice prima dell’invio. Le email automatiche del gestionale le arrivano lì.',
        'Nella pagina di accesso scrive il nome utente e sceglie **@esterni.cidisonlus.org** nel menu accanto, oppure '
          + 'scrive l’indirizzo per intero.',
        'Le **caselle di servizio** (narni@, crosia@…) sono caselle condivise: si danno da «Modifica» → «Caselle di '
          + 'servizio» con «Aggiungi», anche a più persone.',
        'Ai collaboratori esterni si può dare **qualunque ruolo**, come al personale. Nell’importazione da foglio la '
          + 'colonna «è esterno» con «sì» fa lo stesso della spunta.',
      ],
    },
    {
      titolo: 'Deroghe per un collaboratore esterno',
      passi: [
        'Nella riga della persona premi «Modifica»: la voce «Collaboratore esterno: deroghe» mostra la sua casella '
          + '@esterni.',
        '«Riceve da fuori»: la casella riceve anche da indirizzi fuori da CIDIS. «Invia fuori»: può scrivere fuori da '
          + 'CIDIS. Valgono subito, senza «Salva».',
        '«Crea anche la casella cidisonlus.org»: controlla l’indirizzo proposto e premi «Crea la casella». Nasce come '
          + 'casella condivisa con la persona che legge e scrive; la trova in «Posta» accanto alla sua. **Si entra '
          + 'sempre con l’indirizzo @esterni**: la casella cidisonlus.org non è un nome utente.',
      ],
      nota: 'Ogni deroga resta nel registro degli eventi. Per ricevere da fuori serve anche il DNS del dominio '
        + 'esterni.cidisonlus.org: se la ricezione non funziona, avvisa chi gestisce il sistema.',
    },
    {
      titolo: 'Assegnare un incarico',
      passi: [
        'Nella riga della persona premi «Incarichi».',
        'Nel riquadro «Assegna un incarico» scegli il «Ruolo» e il «Tipo di ambito»: tutta CIDIS, una o più sedi '
          + '(oppure «Tutte le sedi»), un’area, un progetto.',
        'Indica la «Scadenza», se serve, e scrivi la «Motivazione».',
        'Premi «Assegna l’incarico».',
      ],
      nota: 'Il gestionale dice quali schede apre l’incarico. «Proroga» sposta la scadenza, «Revoca» chiude '
        + 'l’incarico; tutte e due chiedono il motivo. Un incarico di Project Manager su un progetto apre anche la '
        + 'casella di posta del progetto, se c’è; su tutta CIDIS no.',
    },
    {
      titolo: 'Caselle di posta condivise',
      permesso: 'utente.gestisci',
      testo: [
        'Nella finestra «Modifica» della persona, voce «Caselle di gruppo», dai o togli una casella di gruppo, con il '
          + 'livello «legge e scrive» oppure «legge». Le caselle dei progetti si danno dalla pagina del progetto.',
        'Finché ci sono caselle arrivate dal vecchio server e non ancora associate a nessuno, in cima alla pagina '
          + 'leggi «Ci sono N caselle di posta da associare». «Associale» porta a «Posta» → «Caselle condivise»: '
          + 'vedi il capitolo «Posta».',
      ],
    },
    {
      titolo: 'Referente privacy e DPO',
      testo: [
        'In CIDIS non c’è un DPO: c’è la Referente privacy, con il ruolo «Referente privacy» su tutta CIDIS. Prende '
          + 'le decisioni che il gestionale riserva al DPO: blocchi legali, supervisioni della posta, accensione '
          + 'delle funzioni sotto controllo insieme alla Direzione, testo dell’avviso in calce alla posta.',
        'Il ruolo «DPO» resta per il giorno in cui CIDIS ne nomini uno: decide come la Referente, e basta uno dei due.',
      ],
      nota: 'Senza nessuna Referente privacy e nessun DPO con un incarico attivo, blocchi legali, supervisioni e '
        + 'avviso in calce non si possono chiedere; l’accensione la decide la sola Direzione.',
    },
    {
      titolo: 'Nuovo invito',
      testo: [
        'Se la persona non è ancora entrata (stato «Invitato, non ancora entrato»), «Nuovo invito» nella sua riga '
          + 'prepara un collegamento e un codice nuovi. Quelli di prima non valgono più.',
        'Lo trovi anche per chi è già entrato, quando ha chiesto il recupero della password senza un’email di '
          + 'recupero verificata e la richiesta è stata approvata: consegna collegamento e codice per un’altra via. '
          + 'Quando accetta l’invito, la persona sceglie la password nuova.',
      ],
    },
    {
      titolo: 'Email di recupero e password dimenticate',
      testo: [
        'Ognuno imposta la sua email di recupero da «Il mio profilo». La colonna «Email di recupero» dice chi l’ha '
          + '(«Verificata»), chi deve ancora confermarla e a chi manca; il filtro «Email di recupero» trova chi non l’ha. '
          + 'Nella finestra «Modifica» la leggi, ma non la cambi: la cambia solo la persona.',
        'Chi dimentica la password chiede la reimpostazione dalla pagina di accesso, e la richiesta arriva in '
          + '«Approvazioni» come «Recupero della password». La decide chi gestisce gli utenti, mai la persona stessa.',
      ],
      passi: [
        'Apri «Approvazioni» e leggi la richiesta: con quale indirizzo entra la persona e se ha un’email di recupero '
          + 'verificata.',
        'Se pensi che non l’abbia chiesta lei, premi «Respingi» e scrivi il motivo.',
        'Altrimenti premi «Approva»: con l’email di recupero verificata parte il collegamento per la password nuova, '
          + 'valido un’ora.',
        'Senza email di recupero verificata non parte niente: contatta la persona, poi nella sua riga in «Utenti» premi '
          + '«Nuovo invito».',
      ],
    },
    {
      titolo: 'Disattivare e riattivare',
      passi: [
        'Le persone non si cancellano: quando una persona non deve più entrare, premi «Disattiva» nella sua riga.',
        'Leggi «Che cosa succede»: niente più accessi, incarichi revocati, il suo lavoro resta con il suo nome.',
        'Scrivi il «Motivo della disattivazione» e conferma.',
        'Per farla tornare premi «Riattiva»: si prepara un invito nuovo e gli incarichi si assegnano di nuovo.',
      ],
    },
    {
      titolo: 'Azzerare il secondo fattore',
      passi: [
        'Serve a chi ha perso il telefono e i codici di recupero. Accertati prima che a chiederlo sia proprio quella '
          + 'persona, di persona o al telefono.',
        'Nella sua riga premi «Azzera il secondo fattore» e leggi «Che cosa succede».',
        'Scrivi il «Motivo» e premi «Azzera il secondo fattore». Le sue sessioni si chiudono subito; Direzione, DPO e '
          + 'Referente privacy ricevono un avviso.',
        'Al prossimo accesso la persona entra con la password e può attivare un secondo fattore nuovo.',
      ],
      nota: 'Il proprio secondo fattore non si azzera da qui. Nell’elenco, l’etichetta «Secondo fattore» dice chi '
        + 'l’ha attivato.',
    },
    {
      titolo: 'Importare molte persone da un foglio',
      passi: [
        'Premi «Importa da foglio», poi «Scarica il modello». Una riga per persona, con email personale, ruolo e '
          + 'ambito; al massimo 200. «crea casella» vuota vale sì per i rapporti interni; «indirizzo CIDIS» vuoto '
          + 'prende la proposta dal nome. «è esterno» con «sì»: collaboratore esterno, con l’accesso '
          + '@esterni.cidisonlus.org, scadenza facoltativa e qualunque ruolo.',
        'Premi «Scegli il foglio»: l’anteprima controlla ogni riga e non crea ancora niente.',
        'Quando le righe incluse sono pronte, premi «Importa … persone».',
        'Premi **«Scarica i codici da dettare»** prima di chiudere: dopo non si rivedono.',
      ],
      nota: 'Gli inviti valgono 24 ore: importa a ridosso della consegna dei codici.',
    },
    {
      titolo: 'Chi usava il vecchio gestionale',
      testo: [
        'Gli autori del vecchio archivio sono nell’elenco come «Autore storico (tecnico)» (Stato «Disattivati e '
          + 'storici»). Per farli entrare premi «Dai l’accesso» nella loro riga: si riprende la stessa utenza, con '
          + 'il lavoro che avevano registrato. Se non devono più comparire, «Elimina dall’elenco».',
      ],
    },
    {
      titolo: 'Scaricare l’elenco degli utenti',
      testo: [
        '«Scarica l’elenco», in alto, scarica un foglio con tutte le persone dei filtri scelti, con incarichi, '
          + 'stato e ultimo accesso. Ogni scaricamento resta nel registro eventi.',
      ],
    },
  ],
  avvertenze: [
    {
      quando: '«Non puoi assegnare su questo ambito: è stata aperta una richiesta di approvazione a chi può»',
      cosaFare: 'L’incarico parte quando un’altra persona lo approva, nella pagina «Approvazioni».',
    },
    {
      quando: 'La persona dice che l’invito non funziona.',
      cosaFare: 'Se è scaduto o il codice è stato sbagliato cinque volte, premi «Nuovo invito» e consegna di nuovo '
        + 'collegamento e codice.',
    },
    {
      quando: 'Non riesci a disattivare una persona.',
      cosaFare: 'Non si disattiva la propria utenza, né l’ultima persona che gestisce gli utenti: la finestra '
        + 'spiega il perché.',
    },
    {
      quando: 'L’email dell’invito non arriva.',
      cosaFare: 'Controlla nel «Registro delle email»; intanto manda il collegamento mostrato nel passo 3 per '
        + 'un’altra via.',
    },
  ],
  vediAnche: ['albo-mediatori', 'posta', 'accesso'],
  parole: ['utenti', 'utenza', 'invito', 'incarico', 'ruolo', 'disattiva', 'riattiva', 'importa', 'foglio', 'storico',
    'secondo fattore', 'azzera', 'telefono perso', 'casella cidis', 'email personale', 'email di recupero',
    'password dimenticata', 'recupero'],
}

export default capitolo
